/**
 * The one place that talks to the API.
 *
 * Everything goes through `request`, so the 401-means-signed-out rule is
 * written once: the session cookie can expire at any moment, and every screen
 * has to react the same way rather than each showing its own broken state.
 */

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/** Set by the auth provider so any 401 can bounce the whole app to sign-in. */
let onUnauthenticated: (() => void) | null = null;

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
    // The session is a cookie; nothing here uses a bearer token.
    credentials: 'same-origin',
  });

  if (response.status === 401) {
    onUnauthenticated?.();
    throw new ApiError(401, 'Your session has expired. Sign in again.');
  }

  if (response.status === 204) return undefined as T;

  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      (body as { error?: string } | null)?.error ?? `Request failed (${response.status}).`;
    throw new ApiError(response.status, message);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'DELETE',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
};

/** Builds "?a=1&b=2", dropping empty values. */
export function qs(params: Record<string, string | number | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : '';
}

/** Thumbnail URL for a file. Same origin: the server proxies imgproxy. */
export function thumbUrl(fileId: string, width: number, fit: 'fit' | 'fill' = 'fit'): string {
  return `/api/files/${fileId}/thumb?w=${width}&fit=${fit}`;
}

export function rawUrl(fileId: string): string {
  return `/api/files/${fileId}/raw`;
}

export function downloadUrl(fileId: string): string {
  return `/api/files/${fileId}/download`;
}
